<template>
  <section class="industry-experience-section">
    <div class="container">
      <!-- 顶部经验描述区 -->
      <div class="top-experience-area">
        <div class="text-content">
          <h2 class="main-title">{{ $t("industryExperience.mainTitle") }}</h2>
          <p class="sub-title">{{ $t("industryExperience.subTitle") }}</p>
          <p class="description">{{ $t("industryExperience.description") }}</p>
        </div>
        <div class="image-wrapper">
          <img
            :src="topImage"
            :alt="$t('industryExperience.imageAlt')"
            class="experience-image"
          />
        </div>
      </div>

      <!-- 底部三个特性区 -->
      <div class="features-area">
        <div
          class="feature-item"
          v-for="feature in features"
          :key="feature.iconKey"
        >
          <div class="feature-icon-wrapper">
            <img
              :src="getIconPath(feature.iconKey)"
              :alt="feature.title"
              class="feature-icon"
            />
          </div>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-text">{{ feature.text }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "IndustryExperience",
  data() {
    return {
      // 确保这个图片在 src/assets/images/ 目录下
      topImage:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&fm=jpg&ixlib=rb-4.1.0&q=80&w=1400", // 替换为你的图片路径
    };
  },
  computed: {
    features() {
      // 从 i18n 获取特性列表，这样文本就是响应式的
      const i18nFeatures = this.$t("industryExperience.features");
      if (Array.isArray(i18nFeatures)) {
        return i18nFeatures.map((featureData) => ({
          iconKey: featureData.iconKey, // 'team', 'resources', 'service'
          title: featureData.title, // 从 i18n 获取的标题
          text: featureData.text, // 从 i18n 获取的文本
        }));
      }
      return []; // 如果 i18n 数据不是数组，返回空数组
    },
  },
  methods: {
    getIconPath(iconKey) {
      try {
        return require(`@/assets/${iconKey}.webp`); // 或者 .png
      } catch (e) {
        console.warn(`图标未找到: src/assets/icons/${iconKey}.svg`);
        return "https://via.placeholder.com/80?text=Icon";
      }
    },
  },
};
</script>

<style scoped>
.industry-experience-section {
  padding: 100px 0;
  background-color: #ffffff;
  position: relative;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 顶部经验描述区 */
.top-experience-area {
  display: flex;
  align-items: center; /* 垂直居中 */
  gap: 80px; /* 增加文本和图片之间的间距 */
  margin-bottom: 100px; /* 增加与下方特性区的间距 */
}

.top-experience-area .text-content {
  flex: 1; /* 占据可用空间 */
  max-width: 50%; /* 限制文本区域宽度，给图片留空间 */
}

.top-experience-area .main-title {
  font-size: 2.3em; /* 增大标题 */
  color: #1a1a2e; /* 更深的颜色 */
  margin-bottom: 8px;
  font-weight: 700;
  line-height: 1.2;
}

.top-experience-area .sub-title {
  font-size: 1.25em;
  color: #007bff; /* 使用主题色作为副标题突出显示 */
  margin-bottom: 24px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.top-experience-area .description {
  font-size: 1.1em;
  line-height: 1.8;
  color: #4a5568;
}

.top-experience-area .image-wrapper {
  flex: 1;
  max-width: 50%;
  position: relative;
}

.top-experience-area .image-wrapper::before {
  content: "";
  position: absolute;
  top: -15px;
  right: -15px;
  bottom: 15px;
  left: 15px;
  background-color: #f1f5f9; /* 使用背景色而不是边框，更现代 */
  border-radius: 20px;
  z-index: 0;
  transform: rotate(3deg);
  transition: transform 0.4s ease;
}

.top-experience-area .image-wrapper:hover::before {
  transform: rotate(1deg);
}

.top-experience-area .experience-image {
  width: 100%;
  height: auto;
  border-radius: 20px; /* 增大圆角 */
  object-fit: cover;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 1;
  transition: transform 0.4s ease;
}

.top-experience-area .image-wrapper:hover .experience-image {
  transform: translateY(-5px);
}

/* 底部三个特性区 */
.features-area {
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(280px, 1fr)
  ); /* 响应式三列布局 */
  gap: 40px; /* 特性之间的间距 */
  text-align: center; /* 图标和文本居中 */
  margin-top: 40px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 30px;
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  background-color: #f8f9fa; /* 轻微底色 */
  border: 1px solid transparent;
}

.feature-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  background-color: #ffffff;
  border-color: #e2e8f0;
}

.feature-icon-wrapper {
  width: 80px; /* 稍微缩小图标容器 */
  height: 80px;
  border-radius: 24px; /* 从圆形改为圆角矩形 */
  background-color: #e6f2ff; /* 浅蓝色背景 */
  border: none; /* 移除边框 */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: none; /* 移除内部阴影 */
  transition: transform 0.3s ease, background-color 0.3s ease;
}

.feature-item:hover .feature-icon-wrapper {
  transform: scale(1.1) rotate(5deg);
  background-color: #007bff; /* 悬浮时变蓝 */
}

.feature-icon {
  width: 40px; /* 图标实际大小 */
  height: 40px;
  object-fit: contain;
  transition: filter 0.3s ease;
}

.feature-item:hover .feature-icon {
  filter: brightness(0) invert(1); /* 悬浮时图标变白 */
}

.feature-title {
  font-size: 1.5em;
  color: #1a1a2e;
  margin-bottom: 16px;
  font-weight: 700;
}

.feature-text {
  font-size: 1.05em;
  line-height: 1.7;
  color: #4a5568;
  max-width: 320px; /* 稍微放宽文本宽度 */
  margin: 0 auto; /* 使其在父容器中居中 */
}

/* 响应式调整 */
@media (max-width: 992px) {
  .top-experience-area {
    flex-direction: column; /* 在较小屏幕上堆叠 */
    text-align: center;
  }
  .top-experience-area .text-content,
  .top-experience-area .image-wrapper {
    max-width: 100%;
  }
  .top-experience-area .image-wrapper {
    margin-top: 30px;
  }
}

@media (max-width: 768px) {
  .top-experience-area .main-title {
    font-size: 2em;
  }
  .features-area {
    grid-template-columns: 1fr; /* 单列显示 */
    gap: 50px; /* 单列时间距可以大一些 */
  }
  .feature-title {
    font-size: 1.3em;
  }
}
</style>
